import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Text,
  Calendar,
  Box,
  useCalendarState,
  ColorScheme,
  ThemeProvider,
  neoTheme,
  CompassColor,
  Button,
  BottomSheet,
  BottomSheetOverlay,
  BottomSheetContent,
  BottomSheetBody,
  Heading,
} from "@noom/wax-component-library";
import { useLogger } from "@noom/hive-analytics";
import type { CivilDateProto, CivilDateRangeProto } from "@noom/noom-contracts/noom_contracts/lib/time/civil_time";

import { useShippingData } from "../hooks/useShippingData";
import { MixpanelTypes } from "../common/types";
import { useUpdateFillDate } from "../hooks/useUpdateFillDate";
import { formatDeliveryWindow, civilDateToDate } from "../utils/dateUtils";

export const UpdateRefillPage = () => {
  const navigate = useNavigate();
  const { shippingData } = useShippingData();
  const { updateFillDate, loading: isUpdating, error: updateError } = useUpdateFillDate();
  const [isBottomSheetOpen, setIsBottomSheetOpen] = useState(false);
  const [validatedDeliveryWindow, setValidatedDeliveryWindow] = useState<CivilDateRangeProto | null>(null);
  const mixpanelLogger = useLogger();

  // log when someone visits this page
  useEffect(() => {
    mixpanelLogger.log(MixpanelTypes.OnUpdateRefillPage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // log when we show the error bottom sheet
  useEffect(() => {
    if (updateError) {
      mixpanelLogger.log(MixpanelTypes.RefillUpdateError);
    }
  }, [updateError, mixpanelLogger]);

  const initialDate = useMemo(() => {
    if (shippingData?.fillDate) {
      return civilDateToDate(shippingData.fillDate as CivilDateProto);
    }
    return null;
  }, [shippingData?.fillDate]);

  const selectableDateRange = useMemo(() => {
    const allowedWindow = shippingData?.allowedFillDateWindow as CivilDateRangeProto | undefined;
    if (allowedWindow?.startDate && allowedWindow?.endDate) {
      return {
        start: civilDateToDate(allowedWindow.startDate as CivilDateProto),
        end: civilDateToDate(allowedWindow.endDate as CivilDateProto),
      };
    }
    return undefined;
  }, [shippingData?.allowedFillDateWindow]);

  const calendarState = useCalendarState(initialDate, {
    defaultToToday: false,
    selectableDateRange,
    showPriorSelection: true,
  });

  const [selectedDate, setSelectedDate] = useState<Date | null>(null);

  const handleDateSelect = async (event: { date: Date }) => {
    const newDate = event.date;

    // Check if this is actually a new selection (different from current selectedDate)
    const isSameAsCurrentSelection = selectedDate && 
      newDate.getFullYear() === selectedDate.getFullYear() &&
      newDate.getMonth() === selectedDate.getMonth() &&
      newDate.getDate() === selectedDate.getDate();

    if (isSameAsCurrentSelection) {
      return;
    }

    setSelectedDate(newDate);

    // Check if selected date is different from initial date
    const isDifferentFromInitial = initialDate && (
      newDate.getFullYear() !== initialDate.getFullYear() ||
      newDate.getMonth() !== initialDate.getMonth() ||
      newDate.getDate() !== initialDate.getDate()
    );

    if (isDifferentFromInitial) {
      const newFillDate: CivilDateProto = {
        year: newDate.getFullYear(),
        month: newDate.getMonth() + 1,
        day: newDate.getDate(),
      };

      const response = await updateFillDate(newFillDate, true);

      // The response contains the estimated delivery window nested under nextMedicalShipment
      const responseData = response as { nextMedicalShipment?: { estimatedDeliveryWindow?: CivilDateRangeProto } } | null;
      if (responseData?.nextMedicalShipment?.estimatedDeliveryWindow) {
        setValidatedDeliveryWindow(responseData.nextMedicalShipment.estimatedDeliveryWindow);
      }
    } else {
      // Reset to original delivery window when selecting initial date
      setValidatedDeliveryWindow(null);
    }
  };

  // Check if selected date is different from initial date
  const hasDateChanged = useMemo(() => {
    if (!selectedDate || !initialDate) return false;
    return (
      selectedDate.getFullYear() !== initialDate.getFullYear() ||
      selectedDate.getMonth() !== initialDate.getMonth() ||
      selectedDate.getDate() !== initialDate.getDate()
    );
  }, [selectedDate, initialDate]);

  // Update calendar selection when shipping data loads
  useEffect(() => {
    if (shippingData?.fillDate) {
      const date = civilDateToDate(shippingData.fillDate as CivilDateProto);
      calendarState.setSelectedDate(date);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shippingData?.fillDate]);

  const deliveryWindow = useMemo(() => {
    if (validatedDeliveryWindow) {
      return formatDeliveryWindow(validatedDeliveryWindow);
    }
    if (shippingData?.estimatedDeliveryWindow) {
      return formatDeliveryWindow(shippingData.estimatedDeliveryWindow as CivilDateRangeProto);
    }
    return "Unable to retrieve date";
  }, [validatedDeliveryWindow, shippingData?.estimatedDeliveryWindow]);

  const handleUpdateClick = () => {
    if (!selectedDate) return;
    setIsBottomSheetOpen(true);
  };

  const handleConfirmUpdate = async () => {
    if (!selectedDate) return;

    mixpanelLogger.log(MixpanelTypes.RefillUpdateConfirmed);

    const newFillDate: CivilDateProto = {
      year: selectedDate.getFullYear(),
      month: selectedDate.getMonth() + 1,
      day: selectedDate.getDate(),
    };

    const result = await updateFillDate(newFillDate, false);
    if (result) {
      const toastMessage = encodeURIComponent("Next refill date updated");
      navigate(`/shipping-and-refills?toastMsg=${toastMessage}`);
    }
    // If error, the updateError state will be set and bottom sheet will show error content
  };

  const handleCancelUpdate = () => {
    mixpanelLogger.log(MixpanelTypes.RefillUpdateCancelled);
    navigate("/shipping-and-refills");
  };

  return (
    <ThemeProvider theme={neoTheme}>
      <div style={{ backgroundColor: "white", minHeight: "100vh" }}>
        <div style={{ padding: "24px", maxWidth: "600px", margin: "0 auto" }}>
          <Text size="md">
            Postpone your refill date for up to two weeks, once per refill period.
          </Text>
          <Box marginTop="24px">
            <Calendar
              state={calendarState}
              colorScheme={ColorScheme.primary}
              showEventMarkers={false}
              highlightToday={false}
              onDateSelect={handleDateSelect}
            />
          </Box>
          <Text size="lg" fontWeight="400" marginTop="24px" display="block">
            Estimated delivery window:
          </Text>
          <Text size="lg" fontWeight="500" display="block">
            {deliveryWindow}
          </Text>
          <Text size="md" fontWeight="400" color={CompassColor.grey4} marginTop="8px" display="block">
            Delivery dates vary by state; most orders ship in 3-7 business days. Holidays may cause delays. Once shipped, tracking information will be provided.
          </Text>
          <Button
            colorScheme="primary"
            isFullWidth={true}
            marginTop="24px"
            isDisabled={!hasDateChanged}
            isLoading={isUpdating}
            onClick={handleUpdateClick}
          >
            Update
          </Button>
        </div>
      </div>
      <BottomSheet isOpen={isBottomSheetOpen} onClose={() => setIsBottomSheetOpen(false)}>
        <BottomSheetOverlay />
        <BottomSheetContent>
          <BottomSheetBody>
            {updateError ? (
              <>
                <Heading size="sm" fontWeight="400">
                  We're unable to update your fill date.
                </Heading>
                <Text size="md" fontWeight="400" marginTop="16px">
                  Please reach out to your care coordinator to via chat with your new fill date to continue.
                </Text>
                <Button
                  colorScheme="primary"
                  isFullWidth={true}
                  marginTop="24px"
                  onClick={() => navigate("/shipping-and-refills")}
                >
                  Nevermind
                </Button>
              </>
            ) : (
              <>
                <Heading size="sm" fontWeight="400">
                  Discard Your Vial 28 Days After It's Opened
                </Heading>
                <Text size="md" fontWeight="400" marginTop="16px">
                  Even if your vial still contains medication, discard it after 28 days.  Don't postpone your refill because of leftover medication.
                </Text>
                <Button
                  colorScheme="primary"
                  isFullWidth={true}
                  marginTop="24px"
                  isLoading={isUpdating}
                  onClick={handleConfirmUpdate}
                >
                  Acknowledge
                </Button>
                <Button
                  colorScheme="secondary"
                  isFullWidth={true}
                  marginTop="12px"
                  onClick={handleCancelUpdate}
                >
                  Cancel
                </Button>
              </>
            )}
          </BottomSheetBody>
        </BottomSheetContent>
      </BottomSheet>
    </ThemeProvider>
  );
};
